// **PhaseClock** — the G5 phase indicator's chrome (`client-rounds-phase`, `client-sandbox-screen`
// tasks 5.3-5.5; sketch `prototype/sketches/rounds-client/phase-clock-slot.html`, decided **A + D**:
// a corner pill at rest, mirroring the counter stack, plus a bare remainder riding each inset
// popup's own header while the player is mid-edit — the states the plan's original "no popup
// covers this corner" argument turned out to be wrong about (`prototypes.md`'s finding).
//
// One component, two presentations, so the two mountings can never drift into two different
// readings of the same snapshot:
//
//   * **`'corner'`** — the pill `client-sandbox-screen` requires to match the counter pill exactly
//     (34px — `sandbox-layout.ts`'s `PHASE_CLOCK_HEIGHT_PX`), carrying the phase's name, the round
//     as *n* of *N*, and the remainder.
//   * **`'bare'`** — the remainder alone, for a popup header: "carrying the remainder alone beside
//     the controls those headers already hold" (`client-sandbox-screen`). No phase name, no round
//     — both popups already have their own header content, and the remainder is the one fact the
//     corner pill's absence would otherwise cost.
//
// **`snapshot.known === false` renders nothing, in either variant.** That is the whole mechanism
// behind "no phase seam renders no phase chrome rather than a placeholder": a bare mount, or a
// rounds boot before the first phase read, hands this component the unknown snapshot
// (`useRoundsPhase`'s own contract), and there is no branch here that could turn that into a zero,
// a dash-shaped fabrication or an invented phase name — the component simply does not mount.
//
// **The remainder's placeholder is the one `client-rounds-phase` and `client-sandbox-screen` both
// bind to.** `remainingSeconds === null` is not "zero seconds left", it is "this phase carries no
// player-facing deadline" (the simulating phase, whose end is reported) — so it renders the same
// em dash `ResourceCounter`/`BeeTypeBar` already use for "no source", never an invented duration.
//
// **Urgency is presentation the channel already computed** (`isUrgentRemainder`, task 2.7): this
// component reads `snapshot.urgent` rather than re-deriving the threshold, so the corner pill and
// the bare remainder can never disagree about whether the countdown has crossed it. It is a change
// of emphasis — honey, never `--danger` — because nothing has failed.
//
// **The phase label never invents a name for a phase this build has no surface for.** `PhaseSurface`
// is exhaustive over the classified surfaces plus `'unknown'`; for `'unknown'` the label is the
// server's own `reportedPhase` string, verbatim — the same "render whatever the tournament reports"
// rule `rounds-phase.ts` states for itself.

import type { CSSProperties } from 'react';
import type { PhaseSurface, RoundsPhaseSnapshot } from '../../../app/rounds/rounds-phase';
import { COUNTER_PLACEHOLDER } from './ResourceCounter';

export type PhaseClockVariant = 'corner' | 'bare';

export interface PhaseClockProps {
  snapshot: RoundsPhaseSnapshot;
  /** `'corner'` (default) — the pill matching the counter stack. `'bare'` — the remainder alone,
   * for a popup header. */
  variant?: PhaseClockVariant;
  style?: CSSProperties;
}

/** Presentation labels for the classified surfaces. `'unknown'` is deliberately absent — see
 * {@link phaseLabel} — so this map never needs an entry invented for it. */
const SURFACE_LABEL: Record<Exclude<PhaseSurface, 'unknown'>, string> = {
  gathering: 'Gathering',
  editing: 'Editing',
  simulating: 'Simulating',
  reviewing: 'Reviewing',
  standings: 'Standings',
  cancelling: 'Cancelling',
  cancelled: 'Cancelled',
};

/** The phase's own name, for the caption. Verbatim from the server for a surface this build does
 * not classify, rather than a label invented for it. */
function phaseLabel(snapshot: RoundsPhaseSnapshot): string {
  if (snapshot.surface === 'unknown') {
    return snapshot.reportedPhase ?? 'Between phases';
  }
  return SURFACE_LABEL[snapshot.surface];
}

/**
 * "Round *n* of *N*" — `Home.tsx`'s own membership-card wording — or `null` when the round is not
 * yet known, which the caller renders nothing for rather than a half-sentence.
 *
 * Rounds are 1-indexed at the protocol: a tournament is created straight into round 1's editing
 * phase (`current_round: 1` from the moment `TournamentState::create` runs), and there is no
 * "round 0" or warm-up phase in the machine (ROUNDS-MODE §2's `Sandbox → Simulate → Review`
 * loop starts numbering at 1). So `round <= 0` is never a real "before the first round" state —
 * it can only be an unset/placeholder value — and is treated exactly like `null` rather than
 * being displayed as "Round 0".
 */
function roundLabel(snapshot: RoundsPhaseSnapshot): string | null {
  if (snapshot.round === null || snapshot.round < 1) {
    return null;
  }
  return snapshot.roundsTotal === null
    ? `Round ${snapshot.round}`
    : `Round ${snapshot.round} of ${snapshot.roundsTotal}`;
}

/** `mm:ss`, or the placeholder for a phase with no player-facing deadline. Exported so a screen
 * test can assert the exact string rather than re-deriving the formatting. */
export function formatPhaseRemainder(remainingSeconds: number | null): string {
  if (remainingSeconds === null) {
    return COUNTER_PLACEHOLDER;
  }
  const whole = Math.max(0, Math.round(remainingSeconds));
  const minutes = Math.floor(whole / 60);
  const seconds = whole % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

const CORNER_STYLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  height: 34,
  padding: '0 11px 0 9px',
  borderRadius: 'var(--r-pill)',
  background: 'var(--hud-bg)',
  border: '1.5px solid var(--hud-line)',
  boxShadow: 'var(--shadow-hud)',
  whiteSpace: 'nowrap',
};

const BARE_STYLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 30,
  padding: '0 10px',
  borderRadius: 'var(--r-pill)',
  background: 'var(--paper-1)',
  border: '1.5px solid var(--line-strong)',
  whiteSpace: 'nowrap',
};

/** Emphasis only — honey, never `--danger`. Nothing has failed at the urgent threshold. The full
 * `border` shorthand, not bare `borderColor`: the base styles use the shorthand, and React warns
 * (at error level, on every phase flip) when an update mixes the two forms on one element
 * (E2E round-3 finding N-R3-1, 2026-08-09). */
const URGENT_STYLE: CSSProperties = {
  background: 'var(--honey-200)',
  border: '1.5px solid var(--honey-600)',
};

export function PhaseClock({ snapshot, variant = 'corner', style }: PhaseClockProps) {
  // The unknown snapshot (a bare mount, or a boot before the first phase read) renders NOTHING —
  // not a placeholder, not a zero. This is the whole mechanism behind that guarantee.
  //
  // The join window is the same rule for a different reason (`client-rounds-phase`, "waiting
  // states name their subject and carry the phase clock"): it has no deadline at all, and a
  // placeholder `--:--` here would read as a stopped timer beside the awaiting-start card that
  // already says, in words, that nothing is running yet.
  if (!snapshot.known || snapshot.surface === 'gathering') {
    return null;
  }

  const remainderText = formatPhaseRemainder(snapshot.remainingSeconds);
  // `--accent-on-soft`, not `--honey-700` (UI/UX audit F-06): the urgent pill fills with
  // `--honey-200`, where `--honey-700` measures only 3.94:1. This state was missed by the original
  // audit sweep because the clock is only urgent near the end of a phase — a reminder that a contrast
  // pass has to visit *states*, not just screens.
  const remainderColor = snapshot.urgent ? 'var(--accent-on-soft)' : 'var(--hud-text)';

  if (variant === 'bare') {
    return (
      <div
        data-testid="phase-clock"
        data-variant="bare"
        data-urgent={snapshot.urgent ? 'true' : 'false'}
        style={{ ...BARE_STYLE, ...(snapshot.urgent ? URGENT_STYLE : {}), ...style }}
      >
        <span
          data-testid="phase-clock-remainder"
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 600,
            fontSize: 15,
            fontVariantNumeric: 'tabular-nums',
            color: remainderColor,
          }}
        >
          {remainderText}
        </span>
      </div>
    );
  }

  const round = roundLabel(snapshot);
  const captionColor = snapshot.urgent ? 'var(--accent-on-soft)' : 'var(--hud-text-dim)';

  return (
    <div
      data-testid="phase-clock"
      data-variant="corner"
      data-urgent={snapshot.urgent ? 'true' : 'false'}
      style={{ ...CORNER_STYLE, ...(snapshot.urgent ? URGENT_STYLE : {}), ...style }}
    >
      <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
        <span
          style={{
            fontFamily: 'var(--font-body)',
            fontWeight: 800,
            fontSize: 7.5,
            letterSpacing: 'var(--tracking-caps)',
            textTransform: 'uppercase',
            color: captionColor,
          }}
        >
          {phaseLabel(snapshot)}
        </span>
        {round !== null && (
          <span
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 600,
              fontSize: 11,
              marginTop: 2,
              color: remainderColor,
            }}
          >
            {round}
          </span>
        )}
      </span>
      <span
        data-testid="phase-clock-remainder"
        style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 600,
          fontSize: 17,
          fontVariantNumeric: 'tabular-nums',
          color: remainderColor,
        }}
      >
        {remainderText}
      </span>
    </div>
  );
}
